import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import ValuationChat from './ValuationChat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faCalculator, faExclamationTriangle, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import './ValuationPage.css';
import Footer from '../Footer/FooterWrapper';
import config from '../../config';
import { trackFormSubmission } from '../../utils/analytics';
import axios from '../../axios';
import { useLanguage } from '../../i18n/LanguageContext';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';

// Use the Mapbox token from config.js
const MAPBOX_TOKEN = config.mapbox_token;

const ValuationPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [address, setAddress] = useState('');
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [valuationAddress, setValuationAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [selectedFromSuggestion, setSelectedFromSuggestion] = useState(false);
  const [addressError, setAddressError] = useState('');

  // Function to track address input
  const trackAddressInput = async (address, pageSource) => {
    try {
      await axios.post(`${config.backend_url}/api/track-address`, {
        address,
        page_source: pageSource
      });
    } catch (error) {
      // Silently fail - don't disrupt user experience
      console.debug('Address tracking failed:', error);
    }
  };

  useEffect(() => {
    // Get address from URL parameters
    const urlParams = new URLSearchParams(location.search);
    const addressParam = urlParams.get('address');
    
    if (addressParam) {
      setAddress(decodeURIComponent(addressParam));
      setIsValidAddress(true);
    }
  }, [location.search]);

  // Handle address change with autocomplete
  const handleAddressChange = async (e) => {
    const value = e.target.value;
    setValuationAddress(value);
    setSelectedFromSuggestion(false);
    setAddressError('');

    // Only search if the value is sufficiently long
    if (value.length > 3) {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            value
            )}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
        );
        const data = await response.json();
        setAddressSuggestions(data.features);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    } else {
      setAddressSuggestions([]);
    }
  };

  // When a suggestion is clicked, update address and clear suggestions
  const handleSuggestionClick = (feature) => {
    setValuationAddress(feature.place_name);
    setAddressSuggestions([]);
    setSelectedFromSuggestion(true);
    setAddressError('');
  };

  const handleValuationSubmit = (e) => {
    e.preventDefault();
    
    // Validate address
    if (!valuationAddress.trim()) {
      setAddressError(t('home.addressRequired'));
      return;
    }

    if (!selectedFromSuggestion) {
      setAddressError(t('home.addressSuggestionRequired'));
      return;
    }

    // Track address input for analytics
    trackAddressInput(valuationAddress.trim(), 'valuation_page');

    trackFormSubmission('valuation_button_homepage', {
      event_category: 'Valuation',
      event_label: 'Clicked Valuation Button on Valuation Page',
      address: valuationAddress.trim()
    });
    // Navigate to valuation page with address
    history.push(`/valuation?address=${encodeURIComponent(valuationAddress.trim())}`);
  };

  // If we have a valid address from URL params, show the chat
  if (isValidAddress && address) {
    return (
      <div className="valuation-page">
        <ValuationChat address={address} />
      </div>
    );
  }

  // Otherwise show the valuation form page
  return (
    <>
      <div className="valuation-page-container">


        {/* Valuation Section */}
        <div className="valuation-section-standalone">
        <h2><FontAwesomeIcon icon={faCalculator} className="valuation-page-icon" /> {t('valuationPage.title')}</h2>
        
        <div className="valuation-intro">
          <h3>{t('valuationPage.introTitle')}</h3>
          <p>{t('valuationPage.introBody')}</p>
        </div>
          <div className="valuation-content">
            <form onSubmit={handleValuationSubmit} className="valuation-form-standalone">
              <div className="valuation-input-container-standalone">
                <div className="address-input-wrapper">
                  {/* Show suggestions */}
                  {addressSuggestions.length > 0 && (
                    <ul className="suggestions-list-valuation">
                      {addressSuggestions.map((feature) => (
                        <li
                          key={feature.id}
                          onClick={() => handleSuggestionClick(feature)}
                        >
                          {feature.place_name}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="valuation-address-input-wrapper">
                    <FontAwesomeIcon icon={faLocationDot} className="valuation-address-icon" />
                    <input
                      type="text"
                      placeholder={t('home.addressPlaceholder')}
                      value={valuationAddress}
                      onChange={handleAddressChange}
                      className={`valuation-address-input-standalone ${addressError ? 'input-error' : ''}`}
                    />
                  </div>
                  {/* Address validation error */}
                  {addressError && (
                    <div className="address-validation-error">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      <span>{addressError}</span>
                    </div>
                  )}
                </div>
                <button type="submit" className="button valuation-button-standalone">
                  <FontAwesomeIcon icon={faCalculator} />
                  <span>{t('common.value')}</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="valuation-section-standalone">
          <h3>{t('valuationPage.howTitle')}</h3>
          <p>{t('valuationPage.howBody')}</p>
          <div className="cta-link">
          <Link to="/">
            <button className="cta-button">{t('faq.backHome')}</button>
          </Link>
        </div>
      </div>
        </div>


      <Footer />
    </>
  );
};

export default ValuationPage; 
